import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { DailyResults } from '../../models/daily-results';
import { ResultsService } from '../../services/results.service';

@Component({
  selector: 'app-results',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, DatePickerModule, DatePipe, DecimalPipe],
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.scss'],
})
export class ResultsComponent implements OnInit {
  rows: DailyResults[] = [];
  isLoading = false;
  errorMessage = '';
  since!: Date;
  until!: Date;

  constructor(
    private resultsService: ResultsService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    const { since, until } = this.getDefaultRange();
    this.applyRange(since, until);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['since'] || params['until']) {
        const { since: defaultSince, until: defaultUntil } = this.getDefaultRange();
        const since = params['since'] ? this.parseDate(params['since']) : defaultSince;
        const until = params['until'] ? this.parseDate(params['until']) : defaultUntil;
        this.applyRange(since, until);
      } else {
        const { since, until } = this.getDefaultRange();
        this.applyRange(since, until);
      }

      this.fetchResults();
    });
  }

  private getDefaultRange(): { since: Date; until: Date } {
    const until = this.startOfDay(new Date());
    const since = this.addDays(until, -29);
    return { since, until };
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return this.startOfDay(result);
  }

  private parseDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return this.startOfDay(new Date());
    }
    return new Date(year, month - 1, day);
  }

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private applyRange(since: Date, until: Date): void {
    const normalizedSince = this.startOfDay(since);
    const normalizedUntil = this.startOfDay(until);
    this.since = normalizedSince <= normalizedUntil ? normalizedSince : normalizedUntil;
    this.until = normalizedSince <= normalizedUntil ? normalizedUntil : normalizedSince;
  }

  applyDateFilter(): void {
    const { since: defaultSince, until: defaultUntil } = this.getDefaultRange();
    const since = this.since ? this.startOfDay(this.since) : defaultSince;
    const until = this.until ? this.startOfDay(this.until) : defaultUntil;
    this.applyRange(since, until);
    this.updateQueryParams();
  }

  clearFiltersAndRefresh(): void {
    this.fetchResults();
  }

  exportToCsv(): void {
    if (!this.rows.length) {
      return;
    }

    const headers = [
      'Data',
      'Contas Cadastradas',
      'Vinculos LoL',
      'Vinculos CS',
      'Batalhas LoL',
      'Take Rate LoL (R$)',
      'Batalhas CS',
      'Take Rate CS (R$)',
      'Numero Compras Moedas',
      'Valor Compras Moedas',
      'Numero Saques',
      'Valor Saques',
      'Deposito - Saques',
      'Ticket Medio',
    ];

    const formatNumber = (value: number | null | undefined): string =>
      Number(value || 0).toFixed(2);

    const csvRows = this.rows.map((row) => [
      this.formatDateForDisplay(row.date),
      String(row.accounts_created_count || 0),
      String(row.lol_links_count || 0),
      String(row.cs_links_count || 0),
      String(row.lol_battles_count || 0),
      formatNumber(row.lol_take_rate),
      String(row.cs_battles_count || 0),
      formatNumber(row.cs_take_rate),
      String(row.coin_purchases_count || 0),
      formatNumber(row.coin_purchases_value),
      String(row.withdrawals_count || 0),
      formatNumber(row.withdrawals_value),
      formatNumber(row.deposit_vs_withdraw_diff),
      formatNumber(row.avg_ticket),
    ]);

    const csv = [headers, ...csvRows]
      .map((line) => line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const since = this.formatDateForApi(this.since);
    const until = this.formatDateForApi(this.until);

    link.href = url;
    link.download = `resultados_${since}_a_${until}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  }

  private updateQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        since: this.formatDateForApi(this.since),
        until: this.formatDateForApi(this.until),
      },
    });
  }

  fetchResults(): void {
    this.isLoading = true;
    this.errorMessage = '';
    const { queryParamMap } = this.route.snapshot;
    const since = queryParamMap.get('since');
    const until = queryParamMap.get('until');
    const params: { since?: string; until?: string } = {};

    if (since) {
      params.since = since;
    }
    if (until) {
      params.until = until;
    }

    this.resultsService.getDailyResults(params).subscribe({
      next: (response) => {
        this.rows = (response ?? []).map((item) => ({
          ...item,
          lol_take_rate: Number(item.lol_take_rate || 0),
          cs_take_rate: Number(item.cs_take_rate || 0),
          coin_purchases_value: Number(item.coin_purchases_value || 0),
          withdrawals_value: Number(item.withdrawals_value || 0),
          deposit_vs_withdraw_diff: Number(item.deposit_vs_withdraw_diff || 0),
          avg_ticket: Number(item.avg_ticket || 0),
        }));
        this.isLoading = false;
      },
      error: () => {
        this.rows = [];
        this.errorMessage = 'Não foi possível carregar os resultados.';
        this.isLoading = false;
      },
    });
  }

  private formatDateForDisplay(value: string): string {
    const [year, month, day] = value.split('-');
    if (!year || !month || !day) {
      return value;
    }
    return `${day}/${month}/${year}`;
  }
}
